import React, { useState, useRef } from 'react'

const ThumbnailFan = ({ 
  images = [], 
  maxThumbnails = 4, 
  size = 'small', // 'small', 'medium', 'large'
  onImageClick = null,
  className = ''
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const hoverTimeoutRef = useRef(null)
  const lastHoverPositionRef = useRef({ x: 0, y: 0 })
  const hoverStartTimeRef = useRef(0)
  
  // Limit to maxThumbnails, but show all if less than max
  const displayImages = images.slice(0, maxThumbnails)
  const hasMoreImages = images.length > maxThumbnails
  
  // Size configurations
  const sizeConfig = {
    small: {
      container: 'w-40 h-20', // Optimized for minimal width while maintaining fan effect
      thumbnail: 'w-16 h-16', // Keep thumbnail size for visibility
      offset: 16, // Reduced spacing for tighter layout
      rotation: 15
    },
    medium: {
      container: 'w-72 h-36', // Increased from w-48 to accommodate wider spread
      thumbnail: 'w-24 h-24', // Doubled from w-12 h-12
      offset: 30, // Increased from 12 to spread thumbnails further apart
      rotation: 20
    },
    large: {
      container: 'w-96 h-48', // Increased from w-64 to accommodate wider spread
      thumbnail: 'w-32 h-32', // Doubled from w-16 h-16
      offset: 40, // Increased from 16 to spread thumbnails further apart
      rotation: 25
    }
  }
  
  const config = sizeConfig[size]
  
  const handleImageClick = (image, index) => {
    if (onImageClick) {
      onImageClick(image, index)
    }
  }

  // Enhanced hover handlers to prevent jitter from bottom entry
  const handleMouseEnter = (index, event) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const mouseY = event.clientY - rect.top
    const cardHeight = rect.height
    
    // Disable hover in the lower third of the card to prevent bottom-entry jitter
    const lowerThirdThreshold = cardHeight * 0.67
    if (mouseY > lowerThirdThreshold) {
      return // Ignore hover in lower third
    }
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    
    // Record hover start position and time
    lastHoverPositionRef.current = { x: event.clientX, y: event.clientY }
    hoverStartTimeRef.current = Date.now()
    
    // Set hover state immediately for responsive feel
    setHoveredIndex(index)
  }

  const handleMouseMove = (index, event) => {
    // Only process if this is the currently hovered card
    if (hoveredIndex !== index) return
    
    const currentTime = Date.now()
    const timeSinceHoverStart = currentTime - hoverStartTimeRef.current
    
    // If hover just started (less than 200ms), require significant movement
    if (timeSinceHoverStart < 200) {
      const deltaX = Math.abs(event.clientX - lastHoverPositionRef.current.x)
      const deltaY = Math.abs(event.clientY - lastHoverPositionRef.current.y)
      const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      
      // Require at least 10px movement before allowing hover state changes
      if (totalMovement < 10) {
        return
      }
    }
    
    // Update position for next movement check
    lastHoverPositionRef.current = { x: event.clientX, y: event.clientY }
  }

  const handleMouseLeave = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    
    // Add a small delay before clearing hover state to prevent jitter
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(null)
    }, 100) // 100ms delay
  }
  
  if (displayImages.length === 0) {
    return null
  }
  
  return (
    <div 
      className={`relative ${config.container} flex items-center justify-center ${className}`}
    >
      {displayImages.map((image, index) => {
        const totalImages = displayImages.length
        const isLastImage = index === totalImages - 1
        
        // Calculate rotation and offset for fan effect (left to right)
        // Increase rotation angle when there are only 2 cards for better visibility
        const baseRotation = totalImages === 2 ? config.rotation * 1.5 : config.rotation
        const isThisThumbnailHovered = hoveredIndex === index
        
        // Base fan position
        const baseRotationValue = (index - (totalImages - 1) / 2) * (baseRotation / totalImages)
        const baseTranslateX = (index - (totalImages - 1) / 2) * config.offset
        const baseTranslateY = Math.abs(index - (totalImages - 1) / 2) * 2
        
        // When this specific thumbnail is hovered, raise it in its own angled direction
        const rotation = isThisThumbnailHovered ? 0 : baseRotationValue
        
        // Calculate raise direction based on the card's original angle
        const raiseDistance = 25
        const raiseAngle = baseRotationValue * (Math.PI / 180) // Convert to radians
        
        // Calculate X and Y components of the raise vector
        const raiseX = isThisThumbnailHovered ? Math.sin(raiseAngle) * raiseDistance : 0
        const raiseY = isThisThumbnailHovered ? -Math.cos(raiseAngle) * raiseDistance : 0
        
        const translateX = isThisThumbnailHovered ? baseTranslateX + raiseX : baseTranslateX
        const translateY = isThisThumbnailHovered ? baseTranslateY + raiseY : baseTranslateY
        
        const zIndex = totalImages - index
        
        return (
          <div
            key={index}
            className="absolute cursor-pointer transition-all duration-300 ease-out"
            style={{
              transform: `rotate(${rotation}deg) translateX(${translateX}px) translateY(${translateY}px)`,
              zIndex: zIndex, // Keep original z-index to prevent jitter
              transformOrigin: 'center center'
            }}
            onMouseEnter={(e) => handleMouseEnter(index, e)}
            onMouseMove={(e) => handleMouseMove(index, e)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleImageClick(image, index)}
          >
            <img
              src={image.url || image}
              alt={image.alt || `Thumbnail ${index + 1}`}
              className={`${config.thumbnail} object-cover rounded-lg border-2 border-white shadow-md hover:shadow-lg transition-shadow duration-200`}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
            
            {/* Show "+X more" indicator on the last visible thumbnail */}
            {isLastImage && hasMoreImages && (
              <div className="absolute -bottom-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium shadow-md">
                +{images.length - maxThumbnails}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ThumbnailFan
